import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { useNavigate, Link } from 'react-router-dom';
import { Warning, EnvelopeSimple, LockKey, User, ArrowLeft, CheckCircle } from '@phosphor-icons/react';
import { AuthShell, Field, SubmitBtn } from '../components/auth/AuthShell';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// ── Invite deeplink pre-validation ─────────────────────────────────────────

function InviteExpired() {
  return (
    <AuthShell subtitle="Invitation invalid">
      <div className="flex flex-col items-center gap-4 py-4">
        <div className="w-14 h-14 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center">
          <Warning size={26} className="text-red-400" weight="fill" />
        </div>
        <p className="text-center text-sm text-zinc-400 leading-relaxed">
          This invite link has <span className="text-red-400 font-medium">expired or is invalid</span>.
          <br />
          <Link to="/signup" className="text-amber-400 hover:text-amber-300 transition-colors">Join the waitlist</Link> to request access.
        </p>
      </div>
    </AuthShell>
  );
}

// ── Main AuthPage ───────────────────────────────────────────────────────────

export default function AuthPage() {
  const navigate = useNavigate();
  const { login, register, loading, error, setError } = useApp();

  const query = new URLSearchParams(window.location.search);
  const inviteToken = query.get('invite');
  const resetToken = query.get('reset');

  // Invite deeplink state
  const [inviteState, setInviteState] = useState(
    inviteToken ? 'validating' : null   // null | 'validating' | 'valid' | 'expired'
  );
  const [inviteEmail, setInviteEmail] = useState('');

  // Form mode: 'login' | 'register' | 'forgot' | 'reset' | 'waitlist'
  const [mode, setMode] = useState(() => {
    if (resetToken) return 'reset';
    if (inviteToken) return 'register';
    return 'login';
  });

  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    inviteToken: inviteToken || '',
  });

  // Waitlist-specific state
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistInfo, setWaitlistInfo] = useState('');
  const [waitlistConfirmed, setWaitlistConfirmed] = useState(null);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  // Validate invite on mount
  useEffect(() => {
    if (!inviteToken) return;
    axios.get(`${API}/auth/validate-invite?token=${encodeURIComponent(inviteToken)}`)
      .then(res => {
        setInviteEmail(res.data.email || '');
        setInviteState('valid');
        setForm(f => ({ ...f, email: res.data.email || '' }));
      })
      .catch(() => setInviteState('expired'));
  }, [inviteToken]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setWaitlistInfo('');

    // ── Waitlist submission ──
    if (mode === 'waitlist') {
      setWaitlistLoading(true);
      try {
        const res = await axios.post(`${API}/auth/waitlist`, {
          full_name: form.fullName.trim(),
          email: form.email.trim().toLowerCase(),
        });
        const data = res.data;
        if (data.already_registered) {
          setError(data.message);
        } else if (data.already_on_list) {
          setWaitlistInfo(data.message);
        } else {
          setWaitlistConfirmed({ position: data.position, name: data.full_name });
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
      } finally {
        setWaitlistLoading(false);
      }
      return;
    }

    try {
      if (mode === 'reset') {
        await axios.post(`${API}/auth/reset-password`, {
          token: resetToken,
          new_password: form.password,
        });
        setMode('login');
        setForm(f => ({ ...f, password: '' }));
        navigate('/auth', { replace: true });
        return;
      }

      if (mode === 'forgot') {
        await axios.post(`${API}/auth/forgot-password`, { email: form.email });
        setMode('login');
        return;
      }

      if (mode === 'login') {
        const data = await login(form.email, form.password);
        navigate(data.user?.role === 'admin' ? '/admin' : '/dashboard', { replace: true });
        return;
      }

      // register
      if (form.password !== form.confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
      const data = await register(form.email, form.password, form.inviteToken, form.fullName);
      navigate('/dashboard/persona-setup', {
        replace: true,
        state: { personaId: data.user?.default_persona_id },
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed. Please try again.');
    }
  };

  // While validating invite token, show spinner
  if (inviteState === 'validating') {
    return (
      <AuthShell subtitle="Validating invite…">
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
        </div>
      </AuthShell>
    );
  }

  if (inviteState === 'expired') return <InviteExpired />;

  // ── Waitlist confirmed state ───────────────────────────────────────────
  if (waitlistConfirmed) {
    return (
      <AuthShell subtitle="You're on the list">
        <div className="flex flex-col items-center gap-5 py-4">
          <div className="w-14 h-14 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center">
            <CheckCircle size={28} className="text-green-400" weight="fill" />
          </div>
          <div className="text-center">
            <p className="text-sm text-zinc-300 mb-4">
              Thanks, <span className="text-white font-medium">{waitlistConfirmed.name.split(' ')[0]}</span>! Your spot is confirmed.
            </p>
            <div className="inline-block bg-[#0a0a0a] border border-amber-500/20 rounded-lg px-8 py-4 mb-4">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Your Position</p>
              <p className="text-3xl font-bold text-amber-500">#{waitlistConfirmed.position}</p>
            </div>
            <p className="text-xs text-zinc-500 leading-relaxed">
              We'll email you an invite link when a spot opens up.<br />No action needed on your end.
            </p>
          </div>
          <button
            type="button"
            onClick={() => { setWaitlistConfirmed(null); setMode('login'); }}
            className="text-xs text-zinc-500 hover:text-amber-400 transition-colors flex items-center gap-1"
          >
            <ArrowLeft size={12} /> Back to sign in
          </button>
        </div>
      </AuthShell>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────

  const titles = {
    login: 'Sign in',
    register: inviteToken ? 'Accept your invitation' : 'Create account',
    forgot: 'Reset password',
    reset: 'Set new password',
    waitlist: 'Join the waitlist',
  };

  return (
    <AuthShell subtitle={titles[mode]}>
      {error && (
        <div className="flex items-start gap-2 mb-5 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-xs text-red-300">
          <Warning size={14} className="shrink-0 mt-0.5 text-red-400" weight="fill" />
          {error}
        </div>
      )}
      {waitlistInfo && (
        <div className="flex items-start gap-2 mb-5 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-xs text-amber-300">
          {waitlistInfo}
        </div>
      )}

      <form onSubmit={submit} className="space-y-4">

        {/* Full name — register + waitlist */}
        {(mode === 'register' || mode === 'waitlist') && (
          <Field
            icon={User}
            label="Full Name"
            value={form.fullName}
            onChange={set('fullName')}
            placeholder="Jane Smith"
            autoFocus
          />
        )}

        {/* Email — all modes except reset */}
        {mode !== 'reset' && (
          <Field
            icon={EnvelopeSimple}
            label="Email"
            type="email"
            value={mode === 'register' && inviteToken ? inviteEmail : form.email}
            onChange={mode === 'register' && inviteToken ? undefined : set('email')}
            readOnly={mode === 'register' && !!inviteToken}
            placeholder="you@example.com"
            autoFocus={mode === 'login' || mode === 'forgot'}
          />
        )}

        {/* Password — login, register, reset only */}
        {mode !== 'forgot' && mode !== 'waitlist' && (
          <Field
            icon={LockKey}
            label={mode === 'reset' ? 'New Password' : 'Password'}
            type="password"
            value={form.password}
            onChange={set('password')}
            placeholder="Min. 8 characters"
            autoFocus={mode === 'reset'}
          />
        )}

        {/* Confirm password — register + reset */}
        {(mode === 'register' || mode === 'reset') && (
          <Field
            icon={LockKey}
            label="Confirm Password"
            type="password"
            value={form.confirmPassword}
            onChange={set('confirmPassword')}
            placeholder="Repeat password"
          />
        )}

        {/* Invite token — register without deeplink */}
        {mode === 'register' && !inviteToken && (
          <Field
            label="Invite Token"
            value={form.inviteToken}
            onChange={set('inviteToken')}
            placeholder="Paste your invite token"
          />
        )}

        {/* Forgot password link */}
        {mode === 'login' && (
          <div className="text-right">
            <button
              type="button"
              onClick={() => { setMode('forgot'); setError(''); }}
              className="text-xs text-zinc-500 hover:text-amber-400 transition-colors"
            >
              Forgot password?
            </button>
          </div>
        )}

        <div className="pt-1">
          <SubmitBtn
            loading={mode === 'waitlist' ? waitlistLoading : loading.auth}
            label={
              mode === 'reset' ? 'Set Password' :
              mode === 'forgot' ? 'Send Reset Link' :
              mode === 'login' ? 'Sign In' :
              mode === 'waitlist' ? 'Join Waitlist' :
              'Create Account'
            }
          />
        </div>
      </form>

      {/* Footer links */}
      <div className="mt-5 text-center">
        {mode === 'login' && (
          <div className="flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={() => { setMode('waitlist'); setError(''); setWaitlistInfo(''); }}
              className="text-xs text-zinc-500 hover:text-amber-400 transition-colors"
            >
              Don't have an account? Join the waitlist →
            </button>
          </div>
        )}
        {mode === 'waitlist' && (
          <div className="flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={() => { setMode('register'); setError(''); setWaitlistInfo(''); }}
              className="text-xs text-zinc-500 hover:text-amber-400 transition-colors"
            >
              Have an invite token? Sign up →
            </button>
            <button
              type="button"
              onClick={() => { setMode('login'); setError(''); setWaitlistInfo(''); }}
              className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors flex items-center gap-1"
            >
              <ArrowLeft size={12} /> Back to sign in
            </button>
          </div>
        )}
        {(mode === 'register' || mode === 'forgot') && !inviteToken && (
          <div className="flex flex-col items-center gap-2">
            {mode === 'register' && (
              <button
                type="button"
                onClick={() => { setMode('waitlist'); setError(''); }}
                className="text-xs text-zinc-500 hover:text-amber-400 transition-colors"
              >
                No invite? Join the waitlist instead →
              </button>
            )}
            <button
              type="button"
              onClick={() => { setMode('login'); setError(''); }}
              className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors flex items-center justify-center gap-1"
            >
              <ArrowLeft size={12} /> Back to sign in
            </button>
          </div>
        )}
        {mode === 'reset' && (
          <button
            type="button"
            onClick={() => { navigate('/auth', { replace: true }); }}
            className="text-xs text-zinc-500 hover:text-amber-400 transition-colors flex items-center justify-center gap-1 mx-auto"
          >
            <ArrowLeft size={12} /> Back to sign in
          </button>
        )}
      </div>
    </AuthShell>
  );
}
